import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import api from '@api/api';
import { CommonResponseObj } from '@models/common';
import DashboardAreaChart from '@pages/Home/components/DashboardAreaChart/DashboardAreaChart';
import MetricCard from '@pages/Home/components/MetricCard/MetricCard';
import TreeGraph from '@pages/Home/components/TreeGraph/TreeGraph';
import dataFlowColors from '@pages/Home/models/dataFlowColors';
import { ILink, NODES } from '@pages/Home/models/treeGraph';
import { IRootState } from '@store/reducers';
import { formatNumberWithDigits } from '@utils/common';

import {
  initSellerUtilization,
  initStatusRate,
  initAlarmStatistics,
  initEfficiencyTrend,
  URL,
  serviceStatusRateDataOrder as dataOrder,
} from '../ChargingEquipmentStability';
import type {
  IAlarmCountStatistics,
  IEfficiencyTrend,
  ISellerUtilization,
  IStatusRate,
  TLongTermOptionValue,
} from '@models/home';

const LINKS: ILink[] = [
  // 充電設備稼動率 #B877f9
  // 充電設備健康(無警示)率 #6FD29F
  // 充電設備充電效率 #2068F6
  // 充電設備服務狀態率 #6FD29F
  {
    data: [NODES.PLATFORM(380, 130), NODES.PILE(560, 300)],
    direction: 'right',
    color: [
      dataFlowColors.ChargingEquipmentUtilization,
      dataFlowColors.ChargingEquipmentHealthRate,
    ],
  },
  {
    data: [NODES.PILE(560, 300), NODES.EV(400, 500)],
    direction: 'left',
    color: [
      dataFlowColors.ChargingEquipmentServiceStatusRate,
      dataFlowColors.ChargingEfficiency,
    ],
  },
];

export default function LongTermView({
  longTermOption,
}: {
  longTermOption: TLongTermOptionValue;
}) {
  const { t } = useTranslation();
  const [sellerUtilization, setSellerUtilization] =
    useState<ISellerUtilization>(initSellerUtilization);
  const [statusRate, setStatusRate] = useState<IStatusRate>(initStatusRate);
  const [alarmStatistics, setAlarmStatistics] =
    useState<IAlarmCountStatistics>(initAlarmStatistics);
  const [efficiencyTrend, setEfficiencyTrend] =
    useState<IEfficiencyTrend>(initEfficiencyTrend);
  const sellerId = useSelector((state: IRootState) => state.userSlice.sellerId);

  function getAlarmStatisticsDataSet() {
    return [
      {
        name: t('LongTermView.NoAlert'),
        data: alarmStatistics.timeList.map((_, idx) =>
          Number(
            formatNumberWithDigits(
              ((alarmStatistics.totalGunCountList[idx] -
                alarmStatistics.highSeverityCountList[idx] -
                alarmStatistics.mediumSeverityCountList[idx] -
                alarmStatistics.lowSeverityCountList[idx]) /
                alarmStatistics.totalGunCountList[idx]) *
                100,
              1,
            ),
          ),
        ),
        color: '#E4E7EC',
      },
      {
        name: t('Model.LowWarning'),
        data: alarmStatistics.lowSeverityCountList.map((count, idx) =>
          Number(
            formatNumberWithDigits(
              (count / alarmStatistics.totalGunCountList[idx]) * 100,
              1,
            ),
          ),
        ),
        color: '#98A2B3',
      },
      {
        name: t('Model.MediumWarning'),
        data: alarmStatistics.mediumSeverityCountList.map((count, idx) =>
          Number(
            formatNumberWithDigits(
              (count / alarmStatistics.totalGunCountList[idx]) * 100,
              1,
            ),
          ),
        ),
        color: '#F29500',
      },
      {
        name: t('Model.HighWarning'),
        data: alarmStatistics.highSeverityCountList.map((count, idx) =>
          Number(
            formatNumberWithDigits(
              (count / alarmStatistics.totalGunCountList[idx]) * 100,
              1,
            ),
          ),
        ),
        color: '#FF4D4F',
      },
    ];
  }
  function getStatusRateDataSet() {
    const totalGunCountList = statusRate.dailyStatusCounts.map(
      (item) => item.totalGunCount,
    );
    return dataOrder.map((orderItem) => ({
      name: t(orderItem.name),
      data: statusRate.dailyStatusCounts.map(
        (item, idx) => (item[orderItem.key] / totalGunCountList[idx]) * 100,
      ),
      color: orderItem.color,
    }));
  }

  async function getLongTermData() {
    try {
      const payload = {
        sellerId,
        timeSharing: longTermOption,
      };
      const [
        utilizationRes,
        statusRateRes,
        alarmStatisticsRes,
        efficiencyTrendRes,
      ] = await Promise.all([
        api.post<CommonResponseObj>(URL.SAAS_UTILIZATION, payload),
        api.post<CommonResponseObj>(URL.SAAS_STATUS_RATE, payload),
        api.post<CommonResponseObj>(URL.ALARM_COUNT_STATISTICS, payload),
        api.post<CommonResponseObj>(URL.SAAS_EFFICIENCY_TREND, payload),
      ]);
      const { data: utilizationResData }: { data: ISellerUtilization } =
        utilizationRes.data;
      setSellerUtilization(utilizationResData);

      const { data: statusRateResData }: { data: IStatusRate } =
        statusRateRes.data;
      setStatusRate(statusRateResData);

      const { data: efficiencyTrendData }: { data: IEfficiencyTrend } =
        efficiencyTrendRes.data;
      setEfficiencyTrend(efficiencyTrendData);

      const { data: alarmStatisticsData }: { data: IAlarmCountStatistics } =
        alarmStatisticsRes.data;
      setAlarmStatistics(alarmStatisticsData);
    } catch (error) {
      console.error('getLongTermData error: ', error);
    }
  }

  const statusRateDataSet = getStatusRateDataSet();

  const alarmStatisticsDataSet = getAlarmStatisticsDataSet();

  useEffect(() => {
    getLongTermData();
  }, [longTermOption]);

  return (
    <>
      <TreeGraph connections={LINKS} />
      <div>
        {/* 充電設備稼動率趨勢 */}
        <MetricCard
          title={t('LongTermView.UtilizationTrend')}
          tooltipContent={t('LongTermView.TotalOperationMinsPeriod')}
          position={{ left: '5%', top: '20%' }}
          ringColor={dataFlowColors.ChargingEquipmentUtilization}
          isLongterm
        >
          <DashboardAreaChart
            range={longTermOption}
            chartData={
              !sellerUtilization.timeList.length
                ? undefined
                : {
                    time: sellerUtilization.timeList,
                    dataset: [
                      {
                        name: t('LongTermView.EquipmentUtilization'),
                        data: sellerUtilization.utilizationMap.map(
                          (utilization) =>
                            Number(
                              formatNumberWithDigits(utilization * 100, 1),
                            ),
                        ),
                        // data: sellerUtilization.utilizationMap.map(
                        //   (utilization, idx) => {
                        //     if (
                        //       idx ===
                        //       sellerUtilization.utilizationMap.length - 1
                        //     ) {
                        //       return 7.2;
                        //     }

                        //     return Number(
                        //       formatNumberWithDigits(utilization * 100, 1),
                        //     );
                        //   },
                        // ),
                        color: '#507ACD',
                      },
                    ],
                  }
            }
          />
        </MetricCard>
        {/* 充電設備服務狀態率趨勢 */}
        <MetricCard
          title={t('LongTermView.ServiceStatusTrend')}
          tooltipContent={t('LongTermView.ProportionOperationalGuns')}
          position={{ left: '5%', top: '60%' }}
          ringColor={dataFlowColors.ChargingEquipmentServiceStatusRate}
          isLongterm
        >
          <DashboardAreaChart
            range={longTermOption}
            chartData={
              !statusRate.timeList.length
                ? undefined
                : {
                    time: statusRate.timeList,
                    dataset: statusRateDataSet,
                  }
            }
            customGrid={{
              top: '24%',
            }}
          />
        </MetricCard>
        {/* 充電效率趨勢 */}
        <MetricCard
          title={t('LongTermView.EfficiencyTrend')}
          tooltipContent={t('LongTermView.ActualOutputRatedPower')}
          position={{ left: '55%', top: '20%' }}
          ringColor={dataFlowColors.ChargingEfficiency}
          isLongterm
        >
          <DashboardAreaChart
            range={longTermOption}
            chartData={
              !efficiencyTrend.timeList.length
                ? undefined
                : {
                    time: efficiencyTrend.timeList,
                    dataset: [
                      {
                        name: t('LongTermView.ChargingEfficiency'),
                        data: efficiencyTrend.efficiency.map((efficiency) =>
                          Number(formatNumberWithDigits(efficiency * 100, 1)),
                        ),
                        color: '#507ACD',
                      },
                    ],
                  }
            }
          />
        </MetricCard>
        <MetricCard
          title={t('LongTermView.HealthTrend')}
          tooltipContent={t('LongTermView.HealthyGunsProportion')}
          position={{ left: '55%', top: '60%' }}
          ringColor={dataFlowColors.ChargingEquipmentHealthRate}
          isLongterm
        >
          <DashboardAreaChart
            range={longTermOption}
            chartData={
              !alarmStatistics.timeList.length
                ? undefined
                : {
                    time: alarmStatistics.timeList,
                    dataset: alarmStatisticsDataSet,
                  }
            }
          />
        </MetricCard>
      </div>
    </>
  );
}
